import { Container, Grid, Typography, TextField, Box, Button, CircularProgress, LinearProgress } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import globals from "../globals";
import CardDesign from "../components/carousels/cardsDesign";
import SearchIcon from '@mui/icons-material/Search';
import { styled } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

const CategoryButton = ({ name, onClick, clicked, ...props }) => {

    const CustomButton = styled(Button)(() => ({
        color: (clicked) ? globals.colors.backgroundColor : globals.colors.textColor,
        backgroundColor: (clicked) ? globals.colors.darkBlue : globals.colors.lightGray,
        borderRadius: 10,
        '&:hover': {
            backgroundColor: globals.colors.darkBlue,
            color: globals.colors.backgroundColor,
        },
    }))

    return (
        <CustomButton {...props} onClick={onClick}>{name}</CustomButton>
    )
}

    const CustomTextField = styled(TextField)(() => ({
        '.MuiOutlinedInput-root': { 
            '&.Mui-focused fieldset': {
                borderColor: globals.colors.lightRed, 
            },
        },
    }));

const CoursesPage = () => {
    const navigate = useNavigate();
    const { category } = useParams();

    const [courses, setCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {

        fetch(globals.apiUrl + '/courses/', {
            headers: {
                'Content-Type': 'application/json'
            }
        })
        .then(response => response.json())
        .then(data => {
            setCourses(data);
        })
        .catch(error => {
            console.error('Error:', error);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, []);

const categories = [
    'Английски език',
    'Български език',
    'Бизнес и Маркетинг',
    'Биология',
    'География',
    'Дизайн',
    'Електротехника',
    'Информационни технологии',
    'Испански език',
    'История',
    'Италиански език',
    'Математика',
    'Немски език',
    'Общообразователни предмети (до 4ти клас)',
    'Популярни курсове',
    'Програмиране',
    'Статистика',
    'Счетоводство',
    // 'Турски език',
    'Финанси',
    'Френски език',
    'Химия',
    'Хоби'
];

const categorySlugMap = {
    'Английски език': 'english-language',
    'Български език': 'bulgarian-language',
    'Бизнес и Маркетинг': 'business-and-marketing',
    'Биология': 'biology',
    'География': 'geography',
    'Дизайн': 'design',
    'Електротехника': 'electrical-engineering',
    'Информационни технологии': 'information-technology',
    'Испански език': 'spanish-language',
    'История': 'history',
    'Италиански език': 'italian-language',
    'Математика': 'mathematics',
    'Немски език': 'german-language',
    'Общообразователни предмети (до 4ти клас)': 'general-education-grades-1-4',
    'Популярни курсове': 'popular-courses',
    'Програмиране': 'programming',
    'Статистика': 'statistics',
    'Счетоводство': 'accounting',
    'Финанси': 'finance',
    'Френски език': 'french-language',
    'Химия': 'chemistry',
    'Хоби': 'hobby'
};

function getCategorySlug(categoryLabel) {
    return categorySlugMap[categoryLabel] ?? null;
}

function getCategoryLabelFromSlug(categorySlug) {
    if (!categorySlug) return null;
    const decoded = decodeURIComponent(categorySlug);
    const entry = Object.entries(categorySlugMap).find(([, slug]) => slug === decoded);
    return entry ? entry[0] : null;
}

    // Courses and categories filter
    const [filterObject, setFilterObject] = useState(() => ({
        title: '',
        categories: (() => {
            const labelFromSlug = getCategoryLabelFromSlug(category);
            return labelFromSlug ? new Set([labelFromSlug.toLowerCase()]) : new Set();
        })()
    }));

    useEffect(() => {
        const labelFromSlug = getCategoryLabelFromSlug(category);
        setFilterObject(prev => ({
            ...prev,
            categories: labelFromSlug ? new Set([labelFromSlug.toLowerCase()]) : new Set()
        }));
    }, [category]);

    // Ascedning and descending filter by name and date 
    const [sortBy, setSortBy] = useState('date_descending');

    const handleFilterCourse = (event) => {
        const filterValueCourse = event.target.value

        setFilterObject({ ...filterObject, title: filterValueCourse })
    }

    const handleCategoryClick = (clickedCategoryLabel) => {
        const normalizedCategory = clickedCategoryLabel.toLowerCase();

        const isAlreadySelected = filterObject.categories.has(normalizedCategory);

        if (isAlreadySelected) {
            setFilterObject({ ...filterObject, categories: new Set() });
            navigate('/courses');
            return;
        }

        const slug = getCategorySlug(clickedCategoryLabel);
        if (!slug) {
            setFilterObject({ ...filterObject, categories: new Set([normalizedCategory]) });
            navigate('/courses');
            return;
        }

        setFilterObject({ ...filterObject, categories: new Set([normalizedCategory]) });
        navigate(`/courses/${slug}`);
    }

    const handleChangeSort = (event) => {
        setSortBy(event.target.value)
    }

    const compareCourses = (x, y, order) => {
        if (order == 'asc') {
            if (x < y) {
                return -1
            }
            if (x > y) {
                return 1
            }
        } else {
            if (x < y) {
                return 1
            }
            if (x > y) {
                return -1
            }
        }
        return 0;
    }

    const handleSort = (x, y) => {

        switch (sortBy) {
            case 'name_ascending':
                return compareCourses(x.title, y.title, 'asc')
            case 'name_descending':
                return compareCourses(x.title, y.title, 'desc')
            case 'date_ascending':
                return compareCourses(x.created_at, y.created_at, 'asc')
            case 'date_descending':
                return compareCourses(x.created_at, y.created_at, 'desc')
        }

    }

    const CustomFormControl = styled(FormControl)(() => ({
        backgroundColor: globals.colors.backgroundColor,
        '.MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: globals.colors.lightRed,
            },
            '&.Mui-focused fieldset': {
                borderColor: globals.colors.darkRed,
            },
        },
    }));

    const CustomMenuItem = styled(MenuItem)(() => ({
        '&.Mui-selected': {
            backgroundColor: globals.colors.lightRed,
            color: globals.colors.backgroundColor,
            '&.Mui-selected:hover': {
                backgroundColor: globals.colors.backgroundColor,
                color: globals.colors.textColor,
            },
        }
    }));

    const ResetButton = styled(Button)(() => ({
        color: globals.colors.backgroundColor,
        backgroundColor: globals.colors.lightRed,
        borderRadius: 10,
        '&:hover': {
            backgroundColor: globals.colors.darkRed,
            color: globals.colors.backgroundColor,
        },
    }));

    const hasCategory = (categorySet, categories) => {
        for(const c of categories)
        {
            if(categorySet.has(c.toLowerCase())) {
                return true;
            }
        }
        return false;
    }


    return (
        <Container>
            <Grid container justifyContent={'space-evenly'} textAlign={'center'}>
                <Grid item>
                    {/* <img src="2.png" width={'70%'}></img> */}
                    <Typography sx={{color: globals.colors.lightRed, fontSize: 30}}><strong>Курсове</strong></Typography>
                    <Typography sx={{color: globals.colors.gray, fontSize: 17, mt:2}}><strong>Ние от eAcademiaBg предлагаме голямо разнообразие от курсове за всеки, който иска да учи и да се развива независимо от възрастта!</strong></Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', mt: 5 }}>
                        <SearchIcon fontSize='large' sx={{ color: 'action.active', mr: 1, my: 1 }} />
                        <CustomTextField fullWidth placeholder="Търсете в над 100 курса" value={filterObject.title} onChange={handleFilterCourse} />
                    </Box>
                </Grid>
            </Grid>
            <Grid container justifyContent={'space-evenly'} textAlign={'center'}>
                <Grid item sx={{ mt: 3 }}>
                    {categories.sort().map((cath, i) => {
                        return <CategoryButton clicked={filterObject.categories.has(cath.toLowerCase())} sx={{ mx: 1, my: 1 }} key={i} name={cath} onClick={() => handleCategoryClick(cath)} />
                    })}
                </Grid>
                <Grid container sx={{ ml: 2, mt: 1 }}>
                    <ResetButton onClick={() => {
                        setFilterObject({ ...filterObject, categories: new Set() });
                        navigate('/courses');
                    }}>
                        <ClearIcon sx={{ mr: 1 }} fontSize="medium" style={{ color: globals.colors.backgroundColor }} />ИЗЧИСТВАНЕ НА ФИЛТРИ
                    </ResetButton>
                </Grid>
                <Grid item fullWidth minWidth={120}>
                    <CustomFormControl sx={{ m: 1 }}>
                        <Select
                            value={sortBy}
                            onChange={handleChangeSort}
                            displayEmpty
                        >
                            <CustomMenuItem value={'date_descending'}>Дата на публикуване (Най-нови)</CustomMenuItem>
                            <CustomMenuItem value={'date_ascending'}>Дата на публикуване (Най-стари)</CustomMenuItem>
                            <CustomMenuItem value={'name_ascending'}>Име на курса (А-Я)</CustomMenuItem>
                            <CustomMenuItem value={'name_descending'}>Име на курса (Я-А)</CustomMenuItem>
                        </Select>
                    </CustomFormControl>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item minWidth={300} xs={12}>
                    {isLoading ? (
                        <Box sx={{ width: '100%', mt: 8, mb: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CircularProgress size={56} sx={{ color: globals.colors.lightRed }} />
                        </Box>
                    ) : (
                        <Grid container justifyContent={'space-evenly'} spacing={5} sx={{ mt: 5 }}>
                            {courses.filter(course => (filterObject.title) ? course.title.toLowerCase().includes(filterObject.title.toLowerCase()) : true)
                                .filter(course => (filterObject.categories.size) ? hasCategory(filterObject.categories, course.category) : true)
                                .sort(handleSort)
                                .map(({ id, title, image_url, instructor_name, instructor_image_url, price, duration, capacity, enrolled_students_count }, i) => {
                                    return (<Grid item maxHeight={'350'}><CardDesign key={i} id={id} name={title} image={image_url} author={instructor_name} pic={instructor_image_url} price={price} duration={duration} url={`/course/${id}`} capacity={capacity} enrolled_students_count={enrolled_students_count}></CardDesign></Grid>)
                                })}
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Container>
    )
}

export default CoursesPage